import { DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

import { ICashbacksRedeemed } from 'src/app/model/cashbacks.redeemed';
import { ListView } from 'src/app/model/list.view';
import { LoginService } from 'src/app/services/login.service';
import { WholesalerService } from 'src/app/services/wholesaler.service';
import { AppConstants } from 'src/app/settings/app-constants';
import { URI } from 'src/app/settings/uri-constants';


@Component({
    selector: 'app-enterprise-cashbacks-redeemed',
    templateUrl: './enterprise-cashbacks-redeemed.component.html',
    styleUrl: './enterprise-cashbacks-redeemed.component.css',
})
export class EnterpriseCashbacksRedeemedComponent extends ListView implements OnInit {
    wholesalerService = inject(WholesalerService);
    datePipe = inject(DatePipe);
    loginService = inject(LoginService);
    dataDownload: ICashbacksRedeemed[] = [];
    cashbacksRedeemed: ICashbacksRedeemed[] = [];
    cashbacksRedeemedToday = 0;
    balance = 0;
    wholesalerId: string = '';
    excelDataList = new Array<any>();
    downloadLinkId = 'cashback-redeemed-link-id';
    download = false;
    data: any;
    dateTimeFormat = AppConstants.DATE_TIME_FORMAT;
    CASHBACK_REDEEMED_DEFAULT_DATE = '2024-11-20';

    // Set the minimum to the date Cashback Report was introduced to the system
    // and set maaximum date to today's date.
    readonly minDate = new Date(this.CASHBACK_REDEEMED_DEFAULT_DATE);
    readonly maxDate = new Date();

    sessionUser$ = this.loginService.sessionUser.asObservable();

    constructor(
        private router: Router,
        private toasterService: ToastrService
    ) {
        super();
    }

    ngOnInit() {
        this.addSearchControls();
        this.setDefaultDateFilter();
        this.sessionUser$
            .pipe(take(1))
            .subscribe((user) => {
                if (user?.enterprise?.wholesaler) {
                    this.wholesalerId = user.enterprise.wholesaler.id.toString();
                    this.getData();
                } else {
                    this.toasterService.warning(
                        'This enterprise is not linked to a wholesaler. Please contact Lov.cash Admin.',
                        'Invalid enterprise setup'
                    );
                    this.router.navigate([URI.getRouteNavPath('enterpriseProfile')]);
                }
            });
    }

    getData() {
        this.setPaginationParams();
        this.fetchCashbacksRedeemed();
    }

    getDownloadData() {
        this.download = true;
        let cashbackRedeemedTotal = 0;
        let salesInclusiveTotal = 0;
        this.excelDataList = [];

        this.wholesalerService
            .getCashbacksRedeemed(this.wholesalerId, this.getSearchParams())
            .subscribe({
                next: (res) => {
                    this.data = res.responseBody.data;
                    this.dataDownload = this.data?.cashbacksRedeemed || [];

                    this.dataDownload.forEach((cr) => {
                        cashbackRedeemedTotal += cr.cashbackAmountRedeemed ?? 0;
                        salesInclusiveTotal += cr.orderTotalAmount ?? 0;
                        this.excelDataList.push(this.getExcelRowData(cr));
                    });

                    this.excelDataList.push(this.getEmptyExcelRow('', '', ''));
                    this.excelDataList.push(this.getEmptyExcelRow('', '', ''));

                    this.excelDataList.push(
                        this.getEmptyExcelRow(cashbackRedeemedTotal, salesInclusiveTotal, 'TOTAL'),
                    );

                    this.handleClick();
                },
                error: () => {
                },
            });
    }

    setDefaultDateRange() {
        let fromDate = this.searchForm.get('fromDate').value;
        let toDate = this.searchForm.get('toDate').value;

        // Exit early if both dates are empty
        if (!fromDate && !toDate) {
            return;
        }

        if (fromDate) {
            const formattedFromDate = this.datePipe.transform(fromDate, 'yyyy-MM-dd');
            this.searchForm.get('fromDate').setValue(formattedFromDate);
        }

        if (toDate) {
            const formattedToDate = this.datePipe.transform(toDate, 'yyyy-MM-dd');
            this.searchForm.get('toDate').setValue(formattedToDate);
        }
    }

    setDefaultDateFilter() {
        const formattedDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');

        this.searchForm.get('fromDate').setValue(formattedDate);
        this.searchForm.get('toDate').setValue(formattedDate);
    }

    addSearchControls() {
        // Initialize the newControlsToAdd object with default values
        const defaultControls = {
            fromDate: '',
            toDate: '',
        };

        // Assign default controls to newControlsToAdd
        this.newControlsToAdd = { ...defaultControls };

        // Call this inherited method to add the new controls
        this.addNewControls();
    }

    /**** 1. Public Methods for UI Interactions ****/

    submit() {
        this.getData();
    }

    clearDates() {
        this.searchForm.get('fromDate').setValue('');
        this.searchForm.get('toDate').setValue('');
        this.getData();
    }

    defaultDates() {
        this.setDefaultDateFilter();
        this.getData();
    }

    getCashierRefDisplay(cr: ICashbacksRedeemed): string {
        const cashierRef = cr.cashierRef?.trim();
        const tillId = cr.tillId?.trim();
        const source = cr.sourceSystem?.trim();

        // Hide duplicate ref for RMS rows when ref == till
        if (source === 'RMS' && cashierRef && tillId && cashierRef === tillId) {
            return '—';
        }

        return cashierRef || '—';
    }

    getTillDisplay(cr: ICashbacksRedeemed): string {
        return cr.tillId?.trim() || '—';
    }

    /**** 2. Private Helper Methods for Data Handling and API Calls ****/

    private setPaginationParams() {
        this.searchForm.patchValue({
            start: this.pagination.start,
            length: this.pagination.length,
        });
    }

    private fetchCashbacksRedeemed() {
        this.download = false;
        this.wholesalerService
            .getCashbacksRedeemed(this.wholesalerId, this.getSearchParams())
            .subscribe({
                next: (res) => this.handleSuccess(res),
                error: () => this.handleError(),
                complete: () => this.handleCompletion(),
            });
    }

    private handleSuccess(res: any) {
        const { data, total } = res.responseBody;

        if (!data) return; // Early return if no data is available

        const { cashbacksRedeemed, balance, cashbacksRedeemedToday } = data;

        this.data = data;
        this.balance = balance;
        this.cashbacksRedeemed = cashbacksRedeemed;
        this.cashbacksRedeemedToday = cashbacksRedeemedToday;
        this.pagination.total = total;
    }

    private handleError() {
    }

    private handleCompletion() {
        this.showSearchResultRow = this.cashbacksRedeemed?.length > 0;
    }

    // 3. Utility Methods for Excel Formatting
    private getExcelRowData(cr: ICashbacksRedeemed) {
        const cashier = cr.cashier ?? cr.redeemedByName ?? 'System';
        const cashierRef = this.getCashierRefDisplay(cr);
        const till = this.getTillDisplay(cr);
        const source = cr.sourceSystem ?? '';
        const wholesaler = cr.wholesalerName ?? '';
        const status = cr.rmsStatus ?? '';

        return {
            CUSTOMER: cr.customer,
            'ORDER NR': cr.orderNumber ?? '',
            'INVOICE NR': cr.invoiceNumber ?? '',
            'SALE INCLUSIVE': cr.orderTotalAmount ?? 0,

            CASHIER: cashier,
            'CASHIER REF': cashierRef,
            TILL: till,
            SOURCE: source,
            WHOLESALER: wholesaler,
            STATUS: status,

            DATE: this.datePipe.transform(cr.createdAt, 'd/MM/yyyy - h:mm a'),
            'CASHBACK REDEEMED': cr.cashbackAmountRedeemed ?? 0,
            'RUNNING BALANCE': cr.runningBalance ?? 0,
        };
    }

    private getEmptyExcelRow(totalCashbackRedeemed: any, totalSaleInclusive: any, totalLabel = '') {
        return {
            CUSTOMER: totalLabel,
            'ORDER NR': '',
            'INVOICE NR': '',
            'SALE INCLUSIVE': totalSaleInclusive,
            CASHIER: '',
            'CASHIER REF': '',
            TILL: '',
            SOURCE: '',
            WHOLESALER: '',
            STATUS: '',
            DATE: '',
            'CASHBACK REDEEMED': totalCashbackRedeemed,
            'RUNNING BALANCE': '',
        };
    }

    // 4. Method for Handling Click
    handleClick() {
        document.getElementById(this.downloadLinkId)?.click();
    }

    // 5. Utility Methods for Search Parameters
    getSearchParams() {
        const params = { ...this.searchForm.value };

        if (params.fromDate) {
            params.fromDate = this.datePipe.transform(params.fromDate, 'yyyy-MM-dd');
        }

        if (params.toDate) {
            params.toDate = this.datePipe.transform(params.toDate, 'yyyy-MM-dd');
        }

        return params;
    }
}
